import { storedHtmlDomPurifyConfig } from '@lmc/ui-kit/src/constants/storedHtmlDomPurifyConfig'
import DOMPurify from 'dompurify'

/**
 * Sanitizes HTML the same way as {@link ViewStoredHtml} before injection.
 */
export function sanitizeStoredHtml(html: unknown): string {
  return DOMPurify.sanitize(
    html === undefined || html === null ? '' : String(html),
    storedHtmlDomPurifyConfig
  )
}

/**
 * Plain-text form of stored HTML: DOMPurify-sanitized, then read as text via the
 * DOM so entities (e.g. `&amp;`) decode correctly. Use for length checks and
 * meaningful-content checks, not for display.
 */
export function storedHtmlToPlainText(html: unknown): string {
  const el = document.createElement('div')
  el.innerHTML = sanitizeStoredHtml(html)
  return (el.textContent ?? '').replace(/\xA0/g, ' ').trim()
}

/**
 * True when stored HTML will show meaningful text after the same DOMPurify pass
 * used for rendering (avoids mismatches where regex sees text inside disallowed tags).
 */
export function hasMeaningfulStoredHtml(html: unknown): boolean {
  return storedHtmlToPlainText(html).length > 0
}

/**
 * Converts stored rich-text HTML to plain text while preserving the author's line
 * breaks: a `<br>` becomes a single newline and a block boundary (`</p>`, `</div>`,
 * `</li>`, `</h1-6>`) becomes a blank line. This is for renderers that take plain
 * strings and honour `\n` (e.g. pdfmake), where injecting raw HTML is not possible.
 * Formatting (bold, lists, etc.) is not preserved — only text and line breaks.
 */
export function storedHtmlToPlainTextWithBreaks(html: unknown): string {
  return sanitizeStoredHtml(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n\n')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/&nbsp;|&#160;|\xA0/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
